import { useCallback } from 'react';
import { NotificationType } from '@sg/graphql-schema';

import { adjustWantedTime } from '@order/features/ordering';
import { type OrderingContext } from '@order/features/ordering';

import {
  useOrderFeedbackNotifications,
  useOrderStatusNotificationPreferences,
} from '../../../../hooks';
import { useNotificationType } from '../../../../integrations';
import { useOrderStatusLiveActivity } from '../../../../integrations';

export const usePostOrderNotifications = () => {
  const { scheduleOrderFeedbackNotification } = useOrderFeedbackNotifications();
  const getNotificationType = useNotificationType();

  const { startOrderStatusLiveActivity } = useOrderStatusLiveActivity();
  const { isPushSubscribed } = useOrderStatusNotificationPreferences({ requestPolicy: 'cache-first' }); // prettier-ignore

  const handlePostOrderNotifications = useCallback(
    (context: OrderingContext) => {
      const {
        orderId = '',
        wantedTime = { time: '', offset: 0 },
        cart,
      } = context;

      const orderWantedTime = wantedTime.time.slice(0, 19).replace(' ', 'T');
      const adjustedOrderWantedTime = adjustWantedTime(wantedTime) ?? '';

      const {
        orderChannel = 'pickup',
        locationName = 'sweetgreen',
        canTrackOrderStatus,
      } = cart;

      if (isPushSubscribed && wantedTime) {
        void scheduleOrderFeedbackNotification({
          orderId,
          wantedTime: adjustedOrderWantedTime,
        });
      }

      const { notificationType } = getNotificationType({
        canTrackOrderStatus,
        wantedTime: orderWantedTime,
      });

      if (notificationType === NotificationType.LiveActivity) {
        startOrderStatusLiveActivity({
          orderId,
          orderChannel,
          canTrackOrderStatus,
          locationName,
          wantedTime: adjustedOrderWantedTime,
        });
      }
    },
    [
      getNotificationType,
      isPushSubscribed,
      scheduleOrderFeedbackNotification,
      startOrderStatusLiveActivity,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { handlePostOrderNotifications };
};
